// @flow

import React from 'react';
import _ from 'lodash';
import gql from 'graphql-tag';
import { connect } from 'react-redux';

import { Mutation, Query } from '@apollo/react-components';

import * as Account from 'app/entities/Account';
import * as Profile from 'app/entities/Profile';
import WithCurrentUserInfo from 'app/common/components/WithCurrentUserInfo';
import ZapFolderChooserStateful, {
  type ZapFolderChooserOnChoose,
} from 'app/dashboard/components/ZapFolderChooserStateful';
import { HOME_FOLDER_ID } from 'app/dashboard/utils/constants';
import { arrayToObjectByKey } from 'app/common/CommonUtils';
import { buildCreationUpdater } from 'app/graphql/utils/buildUpdaters';
import { fullFoldersQuery } from 'app/graphql/structures/Folder';
import { selectFlow } from 'app/beta-editor/Selectors';
import {
  canMoveZapToFolder,
  sortFoldersByTitleWithHome,
} from 'app/dashboard/utils/DashboardUtils';

type Props = {
  zapId: number,
  onChoose: ZapFolderChooserOnChoose,
};

const mapStateToProps = (state, ownProps) => {
  const zap = selectFlow(ownProps.zapId, state) || {};
  const isZapOwnedByCurrentUser =
    zap.owner_id === Profile.selectors.all.currentProfileId(state);
  const folders = sortFoldersByTitleWithHome(ownProps.folders).filter(
    _.partial(canMoveZapToFolder, {
      isOwnerOfZap: isZapOwnedByCurrentUser,
    })
  );
  return {
    canShowSharedFolders: Account.selectors.isTeam(
      Account.selectors.all.currentAccount(state)
    ),
    // If `folder_ids` is empty, then it's in the home folder.
    // Add the special ID here to treat the home folder less specially.
    currentFolderIds: _.isEmpty(zap.folder_ids)
      ? [HOME_FOLDER_ID]
      : zap.folder_ids,
    defaultOpen: zap.is_shared ? 'shared' : 'private',
    folders,
    isZapShared: zap.is_shared,
  };
};

const ZapFolderChooserStatefulContainer = connect(mapStateToProps)(
  ZapFolderChooserStateful
);

const buildCreationArgs = (accountId, title) => ({
  variables: {
    input: {
      accountId,
      title,
    },
  },
});

export const createFolderMutation = gql`
  mutation ZapFolderChooserCreateFolder($input: CreateFolderInput!) {
    createFolder(input: $input) {
      accountId
      id
      isShared
      members {
        email
        id
        imageUrl
        isOwner
        name
      }
      owner {
        email
        id
        imageUrl
        name
      }
      teams {
        id
        imageUrl
        memberCount
        name
      }
      title
    }
  }
`;

const buildCreateFolderUpdater = buildCreationUpdater({
  queryKey: 'folders',
  mutationKey: 'createFolder',
});

const QueriedZapFolderChooserStatefulContainer = (props: Props) => {
  return (
    <WithCurrentUserInfo>
      {({ accountId }) => (
        <Query query={fullFoldersQuery} variables={{ accountId }}>
          {({ data = {} }) => (
            <Mutation
              mutation={createFolderMutation}
              update={buildCreateFolderUpdater(fullFoldersQuery, { accountId })}
            >
              {createFolder => {
                const onCreateFolder = _.flow(
                  _.partial(buildCreationArgs, accountId),
                  createFolder
                );
                return (
                  <ZapFolderChooserStatefulContainer
                    {...props}
                    folders={arrayToObjectByKey(
                      'id',
                      _.get(data, ['account', 'folders'])
                    )}
                    onCreateFolder={onCreateFolder}
                  />
                );
              }}
            </Mutation>
          )}
        </Query>
      )}
    </WithCurrentUserInfo>
  );
};

export default QueriedZapFolderChooserStatefulContainer;
